/**
 * MenuScene.ts — Main menu Phaser scene
 *
 * Manages the menu overlay visibility and transitions
 * to other scenes. The visual menu itself is HTML/CSS (MenuUI).
 */

import * as Phaser from 'phaser';
import { MenuUI } from '../ui/MenuUI';
import { MultiplayerMenuUI } from '../ui/MultiplayerMenuUI';
import { BotDifficulty } from '../core/bots';

export class MenuScene extends Phaser.Scene {
  private menuUI: MenuUI;
  private multiUI: MultiplayerMenuUI;
  private particles!: Phaser.GameObjects.Graphics;

  // Floating particles for the background
  private dots: Array<{
    x: number;
    y: number;
    vx: number;
    vy: number;
    radius: number;
    alpha: number;
  }> = [];

  constructor() {
    super({ key: 'MenuScene' });
    this.menuUI = new MenuUI();
    this.multiUI = new MultiplayerMenuUI();
  }

  create(): void {
    const { width, height } = this.scale;

    // Create subtle background particles
    this.particles = this.add.graphics();
    this.dots = [];
    for (let i = 0; i < 30; i++) {
      this.dots.push({
        x: Math.random() * width,
        y: Math.random() * height,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
        radius: Math.random() * 1 + 0.3,
        alpha: Math.random() * 0.12 + 0.02,
      });
    }

    // Show the HTML menu
    this.menuUI.show({
      onPractice: (difficulty: BotDifficulty, mode: { team1Count: number; team2Count: number }) => {
        this.menuUI.destroy();
        this.scene.start('PracticeScene', { difficulty, mode });
      },
      onWatchReplay: (data) => {
        this.menuUI.destroy();
        this.scene.start('ReplayScene', { replayData: data });
      },
      onMultiplayer: () => {
        this.menuUI.destroy();
        this.multiUI.show({
          onBack: () => {
            this.multiUI.destroy();
            // Re-show main menu by restarting scene
            this.scene.restart();
          },
          onStartMatch: (isHost: boolean, mode: any) => {
            this.multiUI.destroy();
            this.scene.start('P2PScene', { isHost, mode });
          }
        });
      },
      onCredits: () => {
        // Credits panel is handled by MenuUI
      },
    });
  }

  update(): void {
    const { width, height } = this.scale;
    this.particles.clear();

    for (const dot of this.dots) {
      dot.x += dot.vx;
      dot.y += dot.vy;

      // Wrap around edges
      if (dot.x < 0) dot.x = width;
      if (dot.x > width) dot.x = 0;
      if (dot.y < 0) dot.y = height;
      if (dot.y > height) dot.y = 0;

      this.particles.fillStyle(0xffffff, dot.alpha);
      this.particles.fillCircle(dot.x, dot.y, dot.radius);
    }
  }

  shutdown(): void {
    this.menuUI.destroy();
  }
}
